import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useRecoilState } from 'recoil';

import { Box, Toast, useToast } from '@zillow/constellation-10';
import { isSigningCompleteModalOpenState } from '../../../atoms';
import CountersigningCompleteModal from '../LandlordLeaseView/ListView/LeaseSignPreview/CountersigningCompleteModal';

import Disclaimer from '../../components/Widgets/Disclaimer';
import { Property } from '../../models';
import {
  useAttachedLeaseAcceptanceForUserFromOuidQuery,
  useDisclaimerAcceptanceForUserFromOuidQuery,
  useInfoForUser,
  useLeaseFromOuidQuery,
  useLeaseSummaryFromOuidQuery,
  usePropertyInfoFromOuidQuery,
  useSetAttachedLeaseAcceptanceForUserFromOuidMutation,
  useSetDisclaimerAcceptanceForUserFromOuidMutation,
} from '../../utils/queries';
import LeaseActionButtonGroup from '../../components/LeaseActions/LeaseActionButtonGroup';
import SummaryTitle from '../../components/LeaseSummary/SummaryTitle';
import LeaseSummary from '../../components/LeaseSummary';
import { SUMMARY_VIEW_SUBTYPES } from '../../models/Lease';
import { STRING_CONSTANTS } from '../LandlordLeaseView/SelfServeView/constants';
import { SELF_UPLOAD_SIGN_DISCLAIMER } from '../../components/RequestSignatureModal/constants';
import DocumentPreviewSelector from '../../components/DocumentPreviewSelector';
import LeaseAttachedCheckbox from '../../components/Widgets/LeaseAttachedCheckbox';
import NoAccessLeaseAlert from '../../components/Widgets/NoAccessLeaseAlert';

function ColandlordSelfServeView() {
  const history = useHistory();
  const { enqueueToast } = useToast();
  const queryParams = new URLSearchParams(window.location.search);
  const ouid = queryParams.get('ouid');
  const nestedViewType = queryParams.get('nestedView');

  const infoForUserQuery = useInfoForUser(ouid, !!ouid);
  const leaseQuery = useLeaseFromOuidQuery(ouid, !!ouid);
  const summaryQueryData = useLeaseSummaryFromOuidQuery(ouid, !!ouid);
  const propertyQuery = usePropertyInfoFromOuidQuery(ouid, !!ouid);
  const disclaimerAcceptanceQuery = useDisclaimerAcceptanceForUserFromOuidQuery(ouid, !!ouid);
  const attachedLeaseAcceptanceQuery = useAttachedLeaseAcceptanceForUserFromOuidQuery(ouid, !!ouid);

  const setDisclaimerAcceptanceMutation = useSetDisclaimerAcceptanceForUserFromOuidMutation();
  const setAttachedLeaseAcceptanceMutation = useSetAttachedLeaseAcceptanceForUserFromOuidMutation();

  const leaseId = infoForUserQuery.data?.leaseId ? String(infoForUserQuery.data.leaseId) : '';

  const lease = leaseQuery.data;
  const property = propertyQuery.data ? new Property(propertyQuery.data) : null;
  const summaryData = summaryQueryData.data;
  const summaryText = summaryData?.summaryText || '';
  const summaryActions = summaryData?.summaryActions || [];
  const acceptedDisclaimer = disclaimerAcceptanceQuery.isLoading
    ? undefined
    : (disclaimerAcceptanceQuery.data?.acceptedDisclaimer ?? false);
  const acceptedAttachedLease = attachedLeaseAcceptanceQuery.isLoading
    ? false
    : (attachedLeaseAcceptanceQuery.data?.acceptedAttachedLease ?? false);

  const [canClickCta, setCanClickCta] = useState(false);
  const [acceptedAttachedLeaseCheckbox, setAcceptedAttachedLeaseCheckbox] = useState(false);
  const [isAcceptingDisclaimer, setIsAcceptingDisclaimer] = useState(false);

  const [isSigningCompleteModalOpen, setIsSigningCompleteModalOpen] = useRecoilState(isSigningCompleteModalOpenState);

  useEffect(() => {
    setAcceptedAttachedLeaseCheckbox(acceptedAttachedLease);
  }, [acceptedAttachedLease]);

  const onDocumentClose = useCallback(() => {
    if (!lease) return;
    history.push(lease.getSelfServePath());
  }, [lease, history]);

  const onDocumentError = useCallback(
    (notification: string) => {
      onDocumentClose();
      enqueueToast(<Toast tone="critical">{notification}</Toast>);
    },
    [onDocumentClose, enqueueToast],
  );

  const onToggleCanClickCta = useCallback(() => {
    setCanClickCta((prev) => !prev);
  }, []);

  const onCtaClicked = useCallback(async () => {
    if (!ouid || isAcceptingDisclaimer) return;

    setIsAcceptingDisclaimer(true);

    const promises: Array<Promise<unknown>> = [];

    if (acceptedAttachedLeaseCheckbox) {
      promises.push(setAttachedLeaseAcceptanceMutation.mutateAsync({ getParams: { ouid } }));
    }

    if (canClickCta) {
      promises.push(setDisclaimerAcceptanceMutation.mutateAsync({ getParams: { ouid } }));
    }

    if (promises.length > 0) {
      await Promise.all(promises);
    }

    setIsAcceptingDisclaimer(false);
  }, [
    canClickCta,
    acceptedAttachedLeaseCheckbox,
    ouid,
    isAcceptingDisclaimer,
    setDisclaimerAcceptanceMutation,
    setAttachedLeaseAcceptanceMutation,
  ]);

  const renderPrimaryAction = useCallback(
    (disabled?: boolean) => {
      return summaryActions ? (
        <LeaseActionButtonGroup
          leaseActions={summaryActions}
          fluid
          lease={lease}
          property={property}
          disabled={disabled || isAcceptingDisclaimer}
          data-test="lease-summary-action-button"
          onPrimaryActionClick={acceptedDisclaimer ? null : onCtaClicked}
        />
      ) : null;
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
    [lease, property, summaryActions, acceptedDisclaimer, isAcceptingDisclaimer, onCtaClicked],
  );

  const onToggleAttachedLeaseAccepted = useCallback(() => {
    setAcceptedAttachedLeaseCheckbox((prev) => !prev);
  }, []);

  const renderDisclaimer = useCallback(() => {
    return (
      <Disclaimer
        afterCheckboxContent={
          <>
            <LeaseAttachedCheckbox checked={acceptedAttachedLeaseCheckbox} onClick={onToggleAttachedLeaseAccepted} />
            {renderPrimaryAction(!canClickCta)}
          </>
        }
        label="I agree to these terms before viewing"
        isChecked={canClickCta}
        onClick={onToggleCanClickCta}
        checkboxLabel={SELF_UPLOAD_SIGN_DISCLAIMER}
      />
    );
  }, [
    canClickCta,
    acceptedAttachedLeaseCheckbox,
    onToggleAttachedLeaseAccepted,
    onToggleCanClickCta,
    renderPrimaryAction,
  ]);

  function renderHeader() {
    const propertyAddress = property?.streetAddress;
    const personFirstName = lease?.landlords?.[1]?.firstName;
    const leaseStartDate = lease?.getFriendlyStartDate();

    return (
      <>
        <SummaryTitle {...{ summaryText, propertyAddress, personFirstName, leaseStartDate }} />
        {acceptedDisclaimer != null && (!acceptedDisclaimer ? renderDisclaimer() : renderPrimaryAction(false))}
        <Box css={{ marginBottom: 'tight' }} />
      </>
    );
  }

  function renderNestedView() {
    switch (nestedViewType) {
      case SUMMARY_VIEW_SUBTYPES.PREVIEW:
        return (
          <DocumentPreviewSelector
            leaseId={leaseId}
            lease={lease}
            property={property}
            isColandlord
            onClose={onDocumentClose}
            onError={onDocumentError}
          />
        );
      default:
        return null;
    }
  }

  function handleCloseSigningModal() {
    setIsSigningCompleteModalOpen(false);
  }

  return (
    <>
      {lease && property && (
        <CountersigningCompleteModal
          isOpen={isSigningCompleteModalOpen}
          lease={lease}
          property={property}
          isColandlord={true}
          onClose={handleCloseSigningModal}
        />
      )}
      <>
        {!ouid || leaseQuery.isError ? (
          <NoAccessLeaseAlert />
        ) : (
          <>
            {renderHeader()}
            <LeaseSummary
              isAlwaysExpanded
              isSelfServe
              isColandlord
              categoryDescriptionOverrides={STRING_CONSTANTS.COLANDLORD_CATEGORY_DESCRIPTION_OVERRIDES}
            />
            {renderNestedView()}
          </>
        )}
      </>
    </>
  );
}

export default ColandlordSelfServeView;
